import { z } from 'zod';
import { PROTOKOLL_STATUS } from '../constants.js';
import { beteiligteSchema } from './protokoll-daten.js';

/**
 * Form der Protokolldaten:
 *
 * ```
 * data = {
 *   <abschnittId>: {...}          // Abschnitt vom Typ "standard"
 *   <abschnittId>: [ {...}, ... ] // Abschnitt vom Typ "wiederholbar"
 *   <abschnittId>: [ {...}, ... ] // Abschnitt vom Typ "schluesseltabelle"
 * }
 * ```
 *
 * Unterschriften stehen bewusst **nicht** in `data`: Sie bezeugen den
 * `dataHash` und könnten sonst nicht Teil des gehashten Stands sein.
 */
export const protokollDataSchema = z.record(z.string(), z.unknown());
export type ProtokollData = z.infer<typeof protokollDataSchema>;

export const protokollNrSchema = z
  .string()
  .regex(/^\d{4}-\d{5}$/, 'Protokollnummer muss dem Muster JJJJ-NNNNN entsprechen');

export const protokollSchema = z.object({
  id: z.string().min(1),
  objektId: z.string().min(1, 'Ein Protokoll braucht ein Objekt'),
  /**
   * Übergebene Einheit. Optional, weil Protokolle aus der Zeit vor der
   * Einheitenebene keine haben — neue Protokolle setzen sie immer.
   */
  einheitId: z.string().nullable().optional(),
  templateVersionId: z.string().min(1, 'Ein Protokoll braucht eine Template-Version'),
  protokollNr: z.string().min(1),
  status: z.enum(PROTOKOLL_STATUS),
  art: z.string().min(1).max(100),
  uebergabeDatum: z.string().nullable().optional(),
  data: protokollDataSchema,
  beteiligte: beteiligteSchema.default([]),
  dataHash: z.string().length(64).nullable().optional(),
  pdfKey: z.string().nullable().optional(),
  vorgaengerId: z.string().nullable().optional(),
  vorprotokollId: z.string().nullable().optional(),
  erfasstVon: z.string().nullable().optional(),
  geraeteId: z.string().nullable().optional(),
  aufbewahrungBis: z.string().nullable().optional(),
});
export type Protokoll = z.infer<typeof protokollSchema>;

/**
 * Erlaubte Statusübergänge. `SIGNED` ist eine Einbahnstraße — von dort geht es
 * nur noch weiter nach RENDERED/SENT oder in die Stornierung, niemals zurück
 * in den Entwurf (CLAUDE.md §2).
 */
export const STATUS_UEBERGAENGE: Record<string, readonly string[]> = {
  DRAFT: ['SIGNED', 'STORNIERT'],
  SIGNED: ['RENDERED', 'STORNIERT'],
  RENDERED: ['SENT', 'STORNIERT'],
  SENT: ['STORNIERT'],
  STORNIERT: [],
};

export const uebergangErlaubt = (von: string, nach: string): boolean =>
  von === nach || (STATUS_UEBERGAENGE[von]?.includes(nach) ?? false);
